import React, { useEffect, useReducer, useCallback, useRef, useState } from 'react'
import style from './input.module.scss'
import { Action } from 'types/interface'
import Icon from 'components/Icon/Icon'
import classnames from "classnames"
import { useMappedState, useDispatch } from 'redux-react-hook'
import { SET_SHOW_AREA } from 'store/ActionTypes'
import { setStorage, getStorage } from 'utils/localstorage'

interface Props {
  value: string | number,
  onChange: (e: React.FormEvent) => any,
  onFocus?: (e: React.FormEvent) => any,
  onBlur?: (e: React.FormEvent) => any,
  LeftIcon?: any,
  RightIcon?: any,
  type?: 'text' | 'password',
  placeholder?: string
  className?: string
  hint?: string
  isPhone?: boolean
  selectCode?: (area: any) => any
  isMessage?: boolean
  onSendMessage?: () => any
  startTime?: boolean
  onTimeend?: () => any
  canSendMessage?: boolean
  messageType?: 'email' | 'phone'
}

interface StateType {
  showPasswd: boolean
  hint: string
  showArea: boolean
  curArea: any
}

const initState: StateType = {
  showPasswd: false,
  hint: '',
  showArea: false,
  curArea: null,
}

function reducer (state: StateType, action: Action) {
  const type = action.type
  if (type === 'clickPasswd') {
    return Object.assign({}, state, {
      showPasswd: !state.showPasswd
    })
  }
  if (type === 'clickArea') {
    return Object.assign({}, state, {
      showArea: !state.showArea
    })
  }
  if (type === 'selectCode') {
    return Object.assign({}, state, {
      showArea: false,
      curArea: action.value
    })
  }
  return Object.assign({}, state, {
    [type]: action.value
  })
}

const Input: React.FC<Props> = ({ LeftIcon, RightIcon, type = 'text', value, onChange, placeholder, className, hint, onFocus, onBlur, isPhone, selectCode, isMessage, onSendMessage, startTime, onTimeend, canSendMessage, messageType = 'phone' }) => {
  const [state, dispatch] = useReducer(reducer, initState)
  const { showPasswd, showArea, curArea } = state
  const messageRef = useRef<HTMLDivElement | null>(null)
  const [callingCode, globalShowArea] = useMappedState(state => [
    state.user.callingCode,
    state.user.showArea,
  ])
  const gDispatch = useDispatch()
  const [callingCodeCopy, setCallingCodeCopy] = useState<any>(null)

  const inputClass = classnames(style.input, className, {
    [style['phone-input']]: isPhone
  })
  const areaListClass = classnames(style['area-list'], 'green-scrollbar', {
    [style.active]: showArea && globalShowArea
  })
  const arrowIconClass = classnames(style.icon, {
    [style.rotate]: showArea && globalShowArea
  })

  const messageText = messageType === 'phone' ? '获取短信验证码' : '获取邮箱验证码'

  useEffect(() => {
    dispatch({ type: 'hint', value: hint })
  }, [hint])

  useEffect(() => {
    let area = getStorage('selectArea')
    if (!area && callingCode) {
      area = callingCode.default_option
    } 
    
    if (area) {
      dispatch({ type: 'curArea', value: area })
      selectCode && selectCode(area)
    }
  }, [dispatch, callingCode, selectCode])

  const countDown = useCallback((end) => {
    const ele = messageRef.current
    if (ele) {
      const second = Math.round((end - Date.now()) / 1000)
      if (second >= 0) {
        ele.innerHTML = `${second}s后可重新发送`
      } else {
        ele.innerHTML = messageText
        dispatch({ type: 'canSendMessage', value: true })
        onTimeend && onTimeend()
      }
    }
  }, [onTimeend, messageText])

  useEffect(() => {
    let timmer: NodeJS.Timeout | null = null
    if (startTime) {
      const end = Date.now() + 1000 * 60
      timmer = setInterval(() => {
        countDown(end)
      }, 1000)
    }

    return () => {
      if (timmer) clearInterval(timmer)
    }
  }, [startTime, countDown])

  const handleClickRight = useCallback(()=> {
    if (type === 'password') {
      dispatch({ type: 'clickPasswd' })
    }
  },[type])

  const selectArea = useCallback((area: any) => {
    dispatch({ type: 'selectCode', value: area })
    setStorage('selectArea', area)
    selectCode && selectCode(area)
  }, [dispatch, selectCode])

  const handleSendMessage = useCallback(() => {
    if (!canSendMessage) return
    onSendMessage && onSendMessage()  
  }, [canSendMessage, onSendMessage])

  const switchArea = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (showArea) {
      dispatch({type: 'clickArea'})
      gDispatch({ type: SET_SHOW_AREA, value: false })
    } else {
      dispatch({type: 'clickArea'})
      gDispatch({ type: SET_SHOW_AREA, value: true })
    }
  }, [gDispatch, showArea])

  const searchCountry = useCallback((e: any) => {
    let s = e.currentTarget.value
    if (s && callingCode) {
      s = s.toLowerCase()
      const filterArr = callingCode.list.filter(item => item.country_code.toLowerCase().includes(s))
      const filterArr2 = callingCode.list.filter(item => item.country_name.includes(s))
      const filterArr3 = callingCode.list.filter(item => item.calling_code.includes(s))
      setCallingCodeCopy({
        ...callingCode,
        list: [...filterArr, ...filterArr2, ...filterArr3]
      })
    } else if (!s) {
      setCallingCodeCopy({...callingCode})
    }
  }, [callingCode])

  useEffect(() => {
    if (!globalShowArea) {
      dispatch({ type: 'showArea', value: false })
    }
  }, [globalShowArea])

  useEffect(() => {
    setCallingCodeCopy({...callingCode})
  }, [callingCode])

  return (
    <div
      className={inputClass}>
      {
        LeftIcon 
          ? (
            <div className={style.left}>
              { LeftIcon }
            </div>
          )
          : null
      }
      {
        isPhone
          ? (
            <div className={style['select-area']}>
              <div
                onClick={switchArea}
                className={style['cur-area']}>
                <div>
                  <span>{curArea && curArea.country_code}</span>
                  <span>{curArea && curArea.calling_code}</span>
                </div>
                <Icon icon="iconicon_unfold" className={arrowIconClass} />
              </div>
              <div className={areaListClass}>
                <div 
                  className={style.search} 
                  onClick={(e) => e.stopPropagation()}
                >
                  <input 
                    type="text" 
                    onChange={searchCountry} 
                    placeholder="搜索国家或地区"
                  />
                </div>
                {
                  callingCodeCopy && callingCodeCopy.list
                    ? (
                      callingCodeCopy.list.map(area => (
                        <div 
                          className={style['area-item']}
                          key={area.country_name}
                          onClick={() => selectArea(area)}  
                        >
                          <span>{`${area.country_name}(${area.country_code})`}</span>
                          <span>{area.calling_code}</span>
                        </div>
                      ))
                    )
                    : null
                }
              </div>
            </div>
          ) 
          : null
      }
      <input
        onFocus={onFocus}
        onBlur={onBlur}
        type={showPasswd ? 'text' : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        maxLength={40}
      />
      {
        state.hint
          ? <div className={classnames(style.hint, {
            [style['hint-message']]: isMessage
          })}>
            { hint }
          </div>
          : <div
            onClick={handleClickRight}
            className={style.right}>
            {
              type === 'password'
                ? <Icon 
                  icon={showPasswd 
                    ? 'iconicon_password_unlock' 
                    : 'iconicon_password_lock'} 
                  className={showPasswd ? style.unlock : ''}
                />
                : RightIcon
            }
          </div>
      }
      {
        isMessage 
          ? (
            <div
              ref={messageRef} 
              className={classnames(style.message, {
                [style.disable]: startTime || !canSendMessage
              })}
              onClick={handleSendMessage}  
            >
              { messageText }
            </div>
          )
          : null
      }
    </div>
  )
}

export default Input
